import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import ConfirmationModal from '@/components/ConfirmationModal';
import { PasswordInput } from '@/components/ui/password-input';
import { getApiErrorMessage } from '@/utils/apiError';
import { toast } from 'sonner';
import { getEmailLengthError } from '@/utils/emailValidation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CreateUser = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState(-1);

  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyId: '',
    companyName: '',
    role: ''
  });

  const initialFormData = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyId: '',
    companyName: '',
    role: ''
  };

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    showCancel: true
  });

  const hasFormChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  // Get current user info on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setCurrentUser(response.data);
        
        // If user is company admin (not super admin), auto-set their company
        if (response.data.companyId) {
          setFormData(prev => ({
            ...prev,
            companyId: response.data.companyId,
            companyName: response.data.companyName || ''
          }));
        }
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }
    };
    
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (companySearch && companySearch !== formData.companyName) {
      searchCompanies(companySearch);
    } else if (!companySearch) {
      setFilteredCompanies([]);
      setShowCompanyDropdown(false);
    }
    setSelectedCompanyIndex(-1); // Reset selection when search changes
  }, [companySearch]);

  // ESC key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (confirmModal.isOpen) {
          setConfirmModal({ ...confirmModal, isOpen: false });
        } else {
          handleCancelWithConfirmation();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [formData, confirmModal]);

  const searchCompanies = async (query) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/companies/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setFilteredCompanies(response.data.companies || []);
      if (response.data.companies && response.data.companies.length > 0) {
        setShowCompanyDropdown(true);
      }
    } catch (error) {
      console.error('Error searching companies:', error);
      setFilteredCompanies([]);
    }
  };

  const selectCompany = (company) => {
    setFormData({
      ...formData,
      companyId: company.id,
      companyName: company.companyName
    });
    setCompanySearch(company.companyName);
    setShowCompanyDropdown(false);
    setSelectedCompanyIndex(-1);
  };

  const handleCompanyKeyDown = (e) => {
    if (!showCompanyDropdown || filteredCompanies.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedCompanyIndex(prev => 
          prev < filteredCompanies.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedCompanyIndex(prev => 
          prev > 0 ? prev - 1 : filteredCompanies.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedCompanyIndex >= 0 && selectedCompanyIndex < filteredCompanies.length) {
          selectCompany(filteredCompanies[selectedCompanyIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowCompanyDropdown(false);
        setSelectedCompanyIndex(-1);
        break;
    }
  };

  const handleCancelWithConfirmation = () => {
    if (hasFormChanges()) {
      setConfirmModal({
        isOpen: true,
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to leave? All changes will be lost.',
        type: 'warning',
        confirmText: 'Leave Anyway',
        cancelText: 'Keep Editing',
        onConfirm: () => navigate('/admin/user-settings')
      });
    } else {
      navigate('/admin/user-settings');
    }
  };

  const handleResendVerification = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${BACKEND_URL}/api/auth/send-verify-email`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.data.alreadyVerified) {
        toast.info('Your email is already verified.');
      } else {
        toast.success('Verification email sent. Check your inbox.');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to send verification email.'));
    }
  };

  const handleSubmit = async (e) => {    e.preventDefault();

    // Guard against duplicate submissions while a create request is in flight
    if (loading) return;

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.companyId || !formData.role) {
      setConfirmModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please fill in all required fields.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
      return;
    }

    const emailLengthError = getEmailLengthError(formData.email);
    if (emailLengthError) {
      setConfirmModal({
        isOpen: true,
        title: 'Validation Error',
        message: emailLengthError,
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setConfirmModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Passwords do not match.',
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/admin/users`, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        companyId: formData.companyId,
        role: formData.role
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setConfirmModal({
        isOpen: true,
        title: 'Success',
        message: response.data?.verificationEmail?.queued === false
          ? 'User created successfully, but the verification email could not be sent. Please retry sending it once email delivery is restored.'
          : 'User created successfully! A verification email has been sent to the new user.',
        type: 'success',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => navigate('/admin/user-settings/list')
      });
    } catch (error) {
      console.error('Error creating user:', error);
      const detail = error.response?.data?.detail;
      const errorMessage = getApiErrorMessage(error, 'Failed to create user. Please try again.');
      if (detail?.code === 'EMAIL_NOT_VERIFIED') {
        // Actionable path (same resend endpoint as the dashboard verify banner)
        setConfirmModal({
          isOpen: true,
          title: 'Verify Your Email',
          message: `${errorMessage} Send yourself a verification email to continue.`,
          type: 'warning',
          confirmText: 'Resend verification email',
          cancelText: 'Close',
          showCancel: true,
          onConfirm: handleResendVerification
        });
        return;
      }
      setConfirmModal({
        isOpen: true,
        title: 'Error',
        message: errorMessage,
        type: 'danger',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {}
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold mb-2" style={{ background: '#FF6600', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Create New User
            </h2>
            <p className="text-gray-600">Add a new user to the system</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl p-8 space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter first name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter last name"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="user@company.com"
              />
            </div>

            {/* Role - Moved above Company */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Role <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.role}
                onChange={(e) => {
                  const newRole = e.target.value;
                  setFormData({ 
                    ...formData, 
                    role: newRole,
                    // Clear company if owner selected
                    ...(newRole === 'owner' ? { companyId: '', companyName: '' } : {})
                  });
                  // Clear company search if owner selected
                  if (newRole === 'owner') {
                    setCompanySearch('');
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="">Select role</option>
                <option value="owner">Owner</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </div>

            {/* Company - Only show for super admin (no companyId), hide for company admin */}
            {/* Debug: currentUser={JSON.stringify(currentUser)} */}
            {(!currentUser || !currentUser.companyId) && (
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company {formData.role !== 'owner' && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  required={formData.role !== 'owner'}
                  disabled={formData.role === 'owner'}
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  onKeyDown={handleCompanyKeyDown}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    formData.role === 'owner' ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder={formData.role === 'owner' ? 'N/A for Owner role' : 'Search for a company...'}
                />
                {showCompanyDropdown && filteredCompanies.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCompanies.map((company, index) => (
                      <button
                        key={company.id}
                        type="button"
                        onClick={() => selectCompany(company)}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          index === selectedCompanyIndex 
                            ? 'bg-orange-100 text-orange-900' 
                            : 'hover:bg-orange-50'
                        }`}
                      >
                        {company.companyName}
                      </button>
                    ))}
                  </div>
                )}
                {formData.companyId && (
                  <p className="text-sm text-green-600 mt-1">✓ Company selected: {formData.companyName}</p>
                )}
              </div>
            )}

            {/* Password Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Enter password (min 6 characters)"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters required</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  required
                  minLength={6}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Confirm password"
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-4 pt-6 border-t">
              <button
                type="button"
                onClick={handleCancelWithConfirmation}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
                style={{ background: '#FF6600' }}
              >
                {loading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>

          {/* Confirmation Modal */}
          <ConfirmationModal
            isOpen={confirmModal.isOpen}
            onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
            onConfirm={confirmModal.onConfirm}
            title={confirmModal.title}
            message={confirmModal.message}
            type={confirmModal.type}
            confirmText={confirmModal.confirmText}
            cancelText={confirmModal.cancelText}
            showCancel={confirmModal.showCancel}
          />
        </div>
      </div>
    </AdminLayout>
  );
};

export default CreateUser;
